import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  pageTheme as defaultPageThemes,
  palettes,
  shapes,
  PageTheme,
  UnifiedTheme,
} from '@backstage/theme';
import { alpha } from '@material-ui/core/styles';

import { accent, brand, neutral, status, surfaces } from './clearisticPalette';

export type ClearisticThemeMode = 'light' | 'dark';

/**
 * Builds the Material-UI half of a Clearistic theme.
 *
 * Light and dark are produced by the same function so the two can only differ
 * where `surfaces[mode]` and `status[mode]` differ. The two theme files used to
 * be near-duplicates that had drifted apart, which is how a debug
 * `backgroundColor: 'red'` and a set of unreadable dark-mode alerts survived.
 *
 * The Backstage UI half of the theme lives in `clearistic-bui.css`, built from
 * the same `clearisticPalette` values.
 */
export function createClearisticTheme(mode: ClearisticThemeMode): UnifiedTheme {
  const s = surfaces[mode];
  const st = status[mode];
  const isDark = mode === 'dark';
  const base = isDark ? palettes.dark : palettes.light;

  // Page header gradient. Both stops are dark enough to carry white text.
  const headerFrom = isDark ? brand[900] : brand[800];
  const headerTo = isDark ? brand[800] : brand[700];

  const pageThemes: Record<string, PageTheme> = {};
  for (const key of Object.keys(defaultPageThemes)) {
    pageThemes[key] = {
      ...defaultPageThemes[key],
      // Page headers always render the brand gradient, which is dark in both
      // modes, so the header font colour is white regardless of theme.
      fontColor: neutral[0],
    };
  }
  for (const key of ['home', 'apis'] as const) {
    pageThemes[key] = genPageTheme({
      colors: [headerFrom, headerTo],
      shape: shapes.wave,
      options: { fontColor: neutral[0] },
    });
  }

  return createUnifiedTheme({
    ...createBaseThemeOptions({
      palette: {
        ...base,
        primary: {
          main: s.interactive,
          light: isDark ? brand[300] : brand[600],
          dark: s.interactiveHover,
          contrastText: s.onInteractive,
        },
        secondary: {
          main: isDark ? accent[300] : accent[600],
          light: isDark ? accent[200] : accent[500],
          dark: isDark ? accent[400] : accent[700],
          contrastText: isDark ? neutral[950] : neutral[0],
        },
        grey: {
          50: neutral[50],
          100: neutral[100],
          200: neutral[200],
          300: neutral[300],
          400: neutral[400],
          500: neutral[500],
          600: neutral[600],
          700: neutral[700],
          800: neutral[800],
          900: neutral[900],
        },
        error: {
          main: st.error.main,
          light: st.error.border,
          dark: st.error.fg,
          contrastText: isDark ? neutral[950] : neutral[0],
        },
        warning: {
          main: st.warning.main,
          light: st.warning.border,
          dark: st.warning.fg,
          contrastText: isDark ? neutral[950] : neutral[0],
        },
        success: {
          main: st.success.main,
          light: st.success.border,
          dark: st.success.fg,
          contrastText: isDark ? neutral[950] : neutral[0],
        },
        info: {
          main: st.info.main,
          light: st.info.border,
          dark: st.info.fg,
          contrastText: isDark ? neutral[950] : neutral[0],
        },
        text: {
          primary: s.fg,
          secondary: s.fgSecondary,
          disabled: s.fgDisabled,
          hint: s.fgSecondary,
        },
        background: {
          default: s.app,
          paper: s.paper,
        },
        divider: s.border,

        // --- Backstage-specific palette additions ---
        border: s.border,
        textContrast: s.fg,
        textSubtle: s.fgSecondary,
        textVerySubtle: s.fgDisabled,
        highlight: isDark ? brand[900] : brand[50],
        // `link`/`linkHover` colour body copy, so they need the 4.5:1 step.
        link: s.interactive,
        linkHover: s.interactiveHover,
        errorBackground: st.error.bg,
        warningBackground: st.warning.bg,
        infoBackground: st.info.bg,
        errorText: st.error.fg,
        warningText: st.warning.fg,
        infoText: st.info.fg,
        gold: isDark ? st.warning.main : st.warning.fg,
        status: {
          ok: st.success.main,
          warning: st.warning.main,
          error: st.error.main,
          pending: s.fgSecondary,
          running: s.interactive,
          aborted: s.fgDisabled,
        },
        banner: {
          info: st.info.main,
          error: st.error.main,
          warning: st.warning.main,
          text: neutral[0],
          link: neutral[0],
          closeButtonColor: neutral[0],
        },
        navigation: {
          ...base.navigation,
          background: s.chrome,
          color: alpha(s.onChrome, 0.85),
          indicator: brand[400],
          selectedColor: s.onChrome,
          navItem: {
            hoverBackground: alpha(neutral[0], 0.12),
          },
          submenu: {
            background: isDark ? neutral[900] : neutral[800],
          },
        },
        tabbar: {
          indicator: brand[400],
        },
        pinSidebarButton: {
          icon: alpha(s.onChrome, 0.85),
          background: alpha(neutral[0], 0.12),
        },
        code: {
          background: isDark ? neutral[800] : neutral[50],
        },
      },
    }),

    typography: {
      htmlFontSize: 16,
      fontFamily: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
      h1: { fontSize: 48, fontWeight: 700, marginBottom: 16 },
      h2: { fontSize: 36, fontWeight: 700, marginBottom: 12 },
      h3: { fontSize: 28, fontWeight: 700, marginBottom: 10 },
      h4: { fontWeight: 600, fontSize: 22, marginBottom: 8 },
      h5: { fontWeight: 600, fontSize: 18, marginBottom: 6 },
      h6: { fontWeight: 600, fontSize: 16, marginBottom: 4 },
    },

    pageTheme: pageThemes,
    defaultPageTheme: 'home',

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: s.app,
            color: s.fg,
          },
        },
      },

      MuiAppBar: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: s.chrome,
            color: s.onChrome,
            boxShadow: `0 1px 0 0 ${
              isDark ? alpha(neutral[0], 0.08) : alpha(neutral[950], 0.12)
            }`,
          },
        },
      },

      BackstageHeader: {
        styleOverrides: {
          header: ({ theme }) => ({
            backgroundImage: undefined,
            background: `linear-gradient(to right, ${headerFrom}, ${headerTo})`,
            boxShadow: 'unset',
            paddingBottom: theme.spacing(2),
            color: neutral[0],
          }),
          title: {
            color: neutral[0],
            fontWeight: 700,
          },
          subtitle: {
            // 0.9 alpha over the gradient still clears 4.5:1.
            color: alpha(neutral[0], 0.9),
          },
          type: {
            color: alpha(neutral[0], 0.9),
          },
        },
      },
      BackstageHeaderLabel: {
        styleOverrides: {
          label: {
            color: alpha(neutral[0], 0.9),
          },
          value: {
            color: neutral[0],
          },
        },
      },

      BackstageHeaderTabs: {
        styleOverrides: {
          defaultTab: {
            fontSize: 'inherit',
            textTransform: 'none',
            color: s.fgSecondary,
          },
          selected: {
            color: s.interactive,
          },
        },
      },

      BackstageOpenedDropdown: {
        styleOverrides: {
          icon: {
            '& path': {
              fill: alpha(s.onChrome, 0.85),
            },
          },
        },
      },

      BackstageTable: {
        styleOverrides: {
          root: {
            '&> :first-child': {
              borderBottom: `1px solid ${s.border}`,
              boxShadow: 'none',
            },
            '& th': {
              borderTop: 'none',
              textTransform: 'none',
              fontWeight: 600,
              color: s.fgSecondary,
            },
          },
        },
      },

      BackstageItemCardHeader: {
        styleOverrides: {
          root: {
            // Teal accent, darkened until white text clears 4.5:1 in both modes.
            backgroundImage: `linear-gradient(to right, ${
              isDark ? accent[800] : accent[700]
            }, ${isDark ? accent[700] : accent[600]})`,
            color: neutral[0],
          },
        },
      },

      BackstageSidebarItem: {
        styleOverrides: {
          label: {
            color: alpha(s.onChrome, 0.85),
          },
          selected: {
            color: s.onChrome,
          },
          iconContainer: {
            color: alpha(s.onChrome, 0.85),
          },
        },
      },

      MuiStepIcon: {
        styleOverrides: {
          root: {
            color: s.borderInteractive,
            '&.MuiStepIcon-active': { color: s.interactive },
            '&.MuiStepIcon-completed': { color: s.interactive },
          },
          text: {
            fill: s.onInteractive,
            fontWeight: 600,
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: {
            color: s.fgSecondary,
            '&.MuiStepLabel-active': { color: s.fg, fontWeight: 600 },
            '&.MuiStepLabel-completed': { color: s.fg },
          },
        },
      },

      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            borderWidth: 1,
            borderStyle: 'solid',
          },
          // Each variant pairs the mode's own container colour with the
          // matching on-container text colour. Previously both themes used
          // `palette.<status>.dark` for text, which painted dark red/amber/green
          // onto a dark surface in dark mode at under 2.5:1.
          standardError: {
            backgroundColor: st.error.bg,
            borderColor: st.error.border,
            color: st.error.fgOnBg,
            '& .MuiAlert-icon': { color: st.error.fgOnBg },
          },
          standardWarning: {
            backgroundColor: st.warning.bg,
            borderColor: st.warning.border,
            color: st.warning.fgOnBg,
            '& .MuiAlert-icon': { color: st.warning.fgOnBg },
          },
          standardSuccess: {
            backgroundColor: st.success.bg,
            borderColor: st.success.border,
            color: st.success.fgOnBg,
            '& .MuiAlert-icon': { color: st.success.fgOnBg },
          },
          standardInfo: {
            backgroundColor: st.info.bg,
            borderColor: st.info.border,
            color: st.info.fgOnBg,
            '& .MuiAlert-icon': { color: st.info.fgOnBg },
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 22px',
          },
          containedPrimary: {
            boxShadow: 'none',
            color: s.onInteractive,
            backgroundColor: s.interactive,
            '&:hover': {
              backgroundColor: s.interactiveHover,
              boxShadow: 'none',
            },
          },
          containedSecondary: {
            boxShadow: 'none',
            color: isDark ? neutral[950] : neutral[0],
            '&:hover': { boxShadow: 'none' },
          },
          outlinedPrimary: {
            color: s.interactive,
            borderColor: s.interactive,
            '&:hover': {
              backgroundColor: alpha(s.interactive, isDark ? 0.16 : 0.08),
              borderColor: s.interactiveHover,
            },
          },
          outlinedSecondary: {
            color: isDark ? accent[300] : accent[600],
            borderColor: isDark ? accent[300] : accent[600],
            '&:hover': {
              backgroundColor: alpha(
                isDark ? accent[300] : accent[600],
                isDark ? 0.16 : 0.08,
              ),
            },
          },
          textPrimary: {
            color: s.interactive,
            '&:hover': {
              backgroundColor: alpha(s.interactive, isDark ? 0.16 : 0.08),
            },
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 16,
            backgroundColor: isDark ? neutral[800] : brand[50],
            color: isDark ? brand[200] : brand[800],
            margin: theme.spacing(0.5),
          }),
          deleteIcon: {
            color: isDark ? brand[200] : brand[800],
          },
          outlined: {
            backgroundColor: 'transparent',
            borderColor: s.borderStrong,
            color: s.fg,
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: brand[400],
            height: 3,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            color: s.fgSecondary,
            '&.Mui-selected': { color: s.interactive },
          },
        },
      },

      MuiTypography: {
        styleOverrides: {
          button: { textTransform: 'none' },
        },
      },

      MuiLink: {
        styleOverrides: {
          root: {
            color: s.interactive,
            '&:hover': { color: s.interactiveHover },
          },
        },
      },

      MuiDivider: {
        styleOverrides: {
          root: { backgroundColor: s.border },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            // Control borders fall under WCAG 1.4.11, so they use the 3:1 step
            // rather than the decorative divider colour.
            borderColor: s.borderInteractive,
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: s.fgSecondary,
            '&.Mui-focused': { color: s.interactive },
          },
        },
      },

      MuiCheckbox: {
        styleOverrides: {
          root: { color: s.borderInteractive },
          colorSecondary: {
            '&.Mui-checked': { color: s.interactive },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: { color: s.borderInteractive },
          colorSecondary: {
            '&.Mui-checked': { color: s.interactive },
          },
        },
      },

      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '&[aria-expanded=true]': {
              backgroundColor: alpha(neutral[0], 0.1),
            },
          },
        },
      },

      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: alpha(neutral[950], isDark ? 0.75 : 0.6),
          },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            paddingTop: 6,
            paddingBottom: 6,
            borderRadius: 8,
            border: `1px solid ${s.border}`,
            backgroundColor: s.paper,
          },
          list: {
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            textAlign: 'left',
            minHeight: 48,
            padding: '6px 16px',
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            '&[role="menu"]': {
              display: 'flex',
              flexDirection: 'column',
              paddingTop: 4,
              paddingBottom: 4,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: { display: 'flex' },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? neutral[700] : neutral[900],
            color: neutral[0],
            fontSize: 12,
          },
        },
      },

      MuiSwitch: {
        styleOverrides: {
          root: { padding: 10 },
          switchBase: {
            padding: 12,
            color: neutral[0],
            '&.Mui-checked': { color: neutral[0] },
            '&.Mui-checked + .MuiSwitch-track': {
              backgroundColor: s.interactive,
              opacity: 1,
            },
          },
          thumb: {
            backgroundColor: neutral[0],
            height: 14,
            width: 14,
          },
          track: {
            borderRadius: 9,
            backgroundColor: s.borderInteractive,
            opacity: 1,
          },
        },
      },
    },
  });
}
